import type { ModuleId } from "../core/RunState.ts";
import type { AchievementId } from "../core/MetaState.ts";

/**
 * AI-Module data access (BL-90 / D-219). The 12 modules live as DATA in
 * `public/data/modules.json` (loaded + validated by DataLoader, injected at
 * boot via {@link registerModules}) — this file only holds the types and the
 * lookup API. Adding a module = one JSON row + (if a new effect type) one
 * `MODULE_EFFECTS` entry in ModuleEffects.ts — zero engine edits, mirroring
 * the cards' `CARD_EFFECTS` open-closed pattern.
 */

/**
 * Module effect descriptor. THE DETERMINISM CONTRACT (D-219): `value` is the
 * EXACT number the effect handler applies at run start — no arithmetic between
 * JSON and system (e.g. `range_multiplier` 1.05 is the multiplier itself,
 * `draft_draw_count` 4 is the resulting draw count). Changing a value in
 * modules.json IS a balance change.
 */
export interface ModuleEffect {
  type:
    | "starting_credits"
    | "range_multiplier"
    | "score_multiplier"
    | "synergy_multiplier"
    | "free_first_tower"
    | "revive"
    | "draft_draw_count"
    | "extra_hp"
    | "combo_timeout_multiplier"
    | "skill_slots"
    | "boss_damage_multiplier"
    | "auto_degrade_threshold";
  value: number;
}

export interface AIModule {
  id: ModuleId;
  /** i18n key for the display name (EN+DE in translations.ts). */
  name: string;
  /** i18n key for the effect description (EN+DE in translations.ts). */
  description: string;
  effect: ModuleEffect;
  unlockCondition: AchievementId | null;
}

/** Raw shape of a public/data/modules.json row (pre-validation). */
export interface ModuleJsonEntry {
  id: string;
  name: string;
  description: string;
  effect: { type: string; value: number };
  unlockCondition: string | null;
}

/** Shape of public/data/modules.json. */
export interface ModuleDataFile {
  modules: ModuleJsonEntry[];
}

// Populated from modules.json at boot (main.ts) and in tests via the global
// setup (tests/setup/registerModuleData.ts) — ONE data source, two injectors.
let MODULE_REGISTRY: readonly AIModule[] = [];

/**
 * Inject the loaded module data. Structural validation happens in DataLoader
 * (runtime) and the modules-schema test (CI); this trusts the validated shape.
 */
export function registerModules(data: ModuleDataFile): void {
  MODULE_REGISTRY = data.modules.map((m) => ({
    id: m.id as ModuleId,
    name: m.name,
    description: m.description,
    effect: m.effect as ModuleEffect,
    unlockCondition: m.unlockCondition as AchievementId | null,
  }));
}

/** Get all registered AI Modules. */
export function getAllModules(): readonly AIModule[] {
  return MODULE_REGISTRY;
}

/** Get a specific module by ID. */
export function getModule(id: ModuleId): AIModule | undefined {
  return MODULE_REGISTRY.find((m) => m.id === id);
}

/** Get all default (unlocked from start) modules. */
export function getDefaultModules(): AIModule[] {
  return MODULE_REGISTRY.filter((m) => m.unlockCondition === null);
}

/** Get modules unlocked by a specific achievement. */
export function getModuleByAchievement(
  achievementId: AchievementId,
): AIModule | undefined {
  return MODULE_REGISTRY.find((m) => m.unlockCondition === achievementId);
}

/**
 * Apply a module's effect. Returns a description of what was applied.
 * Pure function — caller decides how to mutate state.
 */
export function applyModule(id: ModuleId): ModuleEffect | null {
  const module = getModule(id);
  return module?.effect ?? null;
}

/**
 * Revoke a module's effect. Returns the effect to reverse.
 * Pure function — caller decides how to mutate state.
 */
export function revokeModule(id: ModuleId): ModuleEffect | null {
  return applyModule(id);
}
